import { PHLoading } from '_/components';
import ConfirmReserveModal from '_/components/ConfirmReserveModal';
import { colors } from '_/constants/theme';
import { useDateContext } from '_/hooks/DateContext';
import { useSlotContext } from '_/hooks/SlotContext';
import { useSpaceContext } from '_/hooks/SpaceContext';
import { AppRoute } from '_/navigation/types';
import { spacesApi } from '_/services/api';
import logger from '_/services/logger';
import { RecurrenceType } from '_/services/models/enums/recurrence-type.enum';
import { SlotsModel } from '_/services/models/slots.model';
import { NavigationProp, useNavigation, useRoute } from '@react-navigation/native';
import { addMinutes, parse, set } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { showMessage } from 'react-native-flash-message';
import styles from './styles';
import { formatDate } from '_/helpers/formatDate';
import { DATE_FORMAT } from '_/config/date';
import { LocationsModel } from '_/services/models/locations.model';
import { SpaceType } from '_/services/models/enums/space-type.enum';

interface ParamsProps {
  slot: SlotsModel;
}

export default function QrReservation() {
  const { t } = useTranslation();
  const navigation = useNavigation<NavigationProp<Record<string, unknown>>>();
  const route = useRoute();
  const { slot } = (route.params ?? {}) as ParamsProps;

  const { setIsSlotModalVisible } = useSlotContext();
  const { setSpaceType, setCurrentSpace, setSpaceId } = useSpaceContext();
  const { setSelectedEventDate, setSelectedDay } = useDateContext();
  const [loading, setLoading] = useState(true);
  const [recurrenceType, setRecurrenceType] = useState<RecurrenceType>(RecurrenceType.ONCE);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const getValidTimeValue = (value: number | null | undefined, fallback: number) =>
    value != null && value >= 0 ? value : fallback;
  useEffect(() => {
    async function init() {
      try {
        const space = await spacesApi.getItem(slot.spaceId);
        setSpaceId(space.id);
        setSpaceType(space.type);
        setCurrentSpace({ ...space, location: { id: space.locationId } as LocationsModel });
        setIsSlotModalVisible(slot.id);
        // setTimeout(0) garante que o setSelectedEventDate rode DEPOIS do efeito
        // de reset do DateProvider (que dispara quando currentSpace.type muda),
        // evitando que os defaults do espaço (ex.: 08:00-18:00 WORK) sobrescrevam
        // o prefill "agora / agora+30min".
        const now = new Date();
        const closeAt = parse(space?.closeAtHourMin ?? '18:00', 'HH:mm', now);
        const suggestedEndHour = space?.metadata?.endHour;
        const suggestedEndMinute = space?.metadata?.endMinute;
        setSelectedDay(formatDate(now, DATE_FORMAT));
        timerRef.current = setTimeout(() => {
          switch (space.type) {
            case SpaceType.MEETING:
            case SpaceType.CALL:
            case SpaceType.STUDIO:
              setSelectedEventDate({ start: now, end: addMinutes(now, 30) });
              break;

            default:
              setSelectedEventDate({
                start: now,
                end: set(now, {
                  hours: getValidTimeValue(suggestedEndHour, closeAt.getHours()),
                  minutes: getValidTimeValue(suggestedEndMinute, closeAt.getMinutes()),
                }),
              });
              break;
          }
        }, 0);
      } catch (error) {
        logger(error);
        showMessage({
          message: t('error'),
          description: t('qrCodeScreen.errorMessages.slotNotAvailable'),
          backgroundColor: colors.errorRed,
        });
        navigation.navigate(AppRoute.HOME);
      } finally {
        setLoading(false);
      }
    }

    init();

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      setIsSlotModalVisible('');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setIsSlotModalVisible('');
    navigation.navigate(AppRoute.HOME);
  };

  return (
    <View style={styles.container}>
      <PHLoading
        loading={loading || !slot}
        type="logo"
        message={t('readingQrCodeScreen.loading')}
      />
      {!loading && slot && (
        <ConfirmReserveModal
          createByQR
          slot={slot}
          onBackdropPress={handleClose}
          onBackButtonPress={handleClose}
          onSwipeStart={handleClose}
          recurrenceType={recurrenceType}
          setRecurrenceType={setRecurrenceType}
        />
      )}
    </View>
  );
}
